// Module 3 / Slice 8 — unit-type catalogue admin screen.
//
// Read-first list of unit_type_catalogue rows; create + edit flows are
// drawer-style follow-up (Phase 4 / C-mod3-9 — inline-edit + bulk-save).
// Each row indicates whether the type is facility-capable (metadata
// flag) so admins can see at a glance which units may host a
// station-facility extension.

import { useQuery } from "@tanstack/react-query";
import { organizationApiClient } from "../../api/client";
import { useI18n } from "../../i18n/I18nContext";

export function UnitTypeCatalogueScreen(): JSX.Element {
  const { t } = useI18n();
  const query = useQuery({
    queryKey: ["organization", "unit-types"],
    queryFn: async () => {
      const response = await organizationApiClient.GET("/organization/unit-types", {
        params: { query: { limit: 200, archived: false } },
      });
      if (!response.data) {
        throw new Error("organization_unit_types_fetch_failed");
      }
      return response.data;
    },
  });

  return (
    <div>
      <h1>{t("organization.unitTypes.heading")}</h1>
      <p className="rc-help">{t("organization.unitTypes.description")}</p>

      {query.isLoading && <p>{t("login.discovering")}</p>}
      {query.isError && (
        <p role="alert" style={{ color: "var(--rc-color-danger)" }}>
          {(query.error as Error).message}
        </p>
      )}
      {query.data && query.data.items && query.data.items.length === 0 && (
        <p>{t("organization.unitTypes.empty")}</p>
      )}
      {query.data && query.data.items && query.data.items.length > 0 && (
        <table className="rc-table">
          <thead>
            <tr>
              <th>{t("organization.unitTypes.column.key")}</th>
              <th>{t("organization.unitTypes.column.displayName")}</th>
              <th>{t("organization.unitTypes.column.sortOrder")}</th>
              <th>{t("organization.unitTypes.column.active")}</th>
            </tr>
          </thead>
          <tbody>
            {query.data.items.map((tp) => (
              <tr key={tp.id} data-testid={`unit-type-row-${tp.key}`}>
                <td><code>{tp.key}</code></td>
                <td>{tp.displayName}</td>
                <td>{tp.sortOrder}</td>
                <td>{tp.isActive ? "\u2714" : "\u2014"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
